import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { initializeSocket, disconnectSocket, getSocket } from '@/lib/socket';
import { useToast } from '@/hooks/useToast';

interface NotificationData {
  id?: string;
  type: string;
  title: string;
  message: string;
  taskId?: string;
  taskTitle?: string;
  commenterName?: string;
  timestamp?: string;
}

export const useRealTimeNotifications = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleNotification = useCallback((data: NotificationData) => {
    // Mostrar toast de notificação
    toast.info(data.title, data.message);

    // Invalidar queries relevantes para atualizar a UI
    queryClient.invalidateQueries({ queryKey: ['notifications'] });

    if (data.taskId) {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task', data.taskId] });
    }
  }, [queryClient, toast]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');

    if (!token) {
      return;
    }

    const socket = initializeSocket(token);

    // Listener para notificações
    socket.on('notification', handleNotification);

    // Listener para confirmação de conexão
    socket.on('connected', (data) => {
      console.log('Real-time notifications enabled:', data);
    });

    return () => {
      socket.off('notification', handleNotification);
      socket.off('connected');
    };
  }, [handleNotification]);

  // Funções para entrar/sair de salas específicas
  const joinTaskRoom = useCallback((taskId: string) => {
    const socket = getSocket();
    if (socket?.connected) {
      socket.emit('join_task', taskId);
    }
  }, []);

  const leaveTaskRoom = useCallback((taskId: string) => {
    const socket = getSocket();
    if (socket?.connected) {
      socket.emit('leave_task', taskId);
    }
  }, []);

  const joinProjectRoom = useCallback((projectId: string) => {
    const socket = getSocket();
    if (socket?.connected) {
      socket.emit('join_project', projectId);
    }
  }, []);

  const leaveProjectRoom = useCallback((projectId: string) => {
    const socket = getSocket();
    if (socket?.connected) {
      socket.emit('leave_project', projectId);
    }
  }, []);

  return {
    joinTaskRoom,
    leaveTaskRoom,
    joinProjectRoom,
    leaveProjectRoom,
    disconnect: disconnectSocket,
  };
};
